/**
 * @file AppIssuesTab.tsx
 * @brief Admin inbox for product bugs and beta feedback submitted from the app.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { Bug, Lightbulb, HelpCircle } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import {
  appFeedbackService,
  type AppFeedback,
  type AppFeedbackStatus,
  type AppFeedbackType,
} from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';

const statusColor = (status: string) => {
  if (status === 'resolved' || status === 'closed') return 'success' as const;
  if (status === 'in_progress') return 'warning' as const;
  return 'default' as const;
};

const typeIcon = (type: AppFeedbackType) => {
  if (type === 'bug') return <Bug size={16} />;
  if (type === 'feedback') return <Lightbulb size={16} />;
  return <HelpCircle size={16} />;
};

const AppIssuesTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useSnackbar();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [notes, setNotes] = useState<Record<string, string>>({});

  const { data: items = [], isLoading, isError } = useQuery({
    queryKey: [...QUERY_KEYS.appFeedbackAll, statusFilter, typeFilter],
    queryFn: () =>
      appFeedbackService.list({
        status: statusFilter === 'all' ? undefined : (statusFilter as AppFeedbackStatus),
        type: typeFilter === 'all' ? undefined : (typeFilter as AppFeedbackType),
      }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { status?: AppFeedbackStatus; adminNotes?: string } }) =>
      appFeedbackService.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.appFeedbackAll });
      showSuccess('Report updated');
    },
    onError: (error: unknown) => {
      showError(error instanceof Error ? error.message : 'Failed to update report');
    },
  });

  const setStatus = (item: AppFeedback, status: AppFeedbackStatus) => {
    updateMutation.mutate({
      id: item.id,
      payload: { status, adminNotes: notes[item.id] ?? item.adminNotes },
    });
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
        App issues
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Bugs and feedback submitted by users from the in-app report form.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <AppSelect
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(String(e.target.value))}
          size="small"
          sx={{ minWidth: 160 }}
        >
          <AppMenuItem value="all">All statuses</AppMenuItem>
          <AppMenuItem value="open">Open</AppMenuItem>
          <AppMenuItem value="in_progress">In progress</AppMenuItem>
          <AppMenuItem value="resolved">Resolved</AppMenuItem>
          <AppMenuItem value="closed">Closed</AppMenuItem>
        </AppSelect>
        <AppSelect
          label="Type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(String(e.target.value))}
          size="small"
          sx={{ minWidth: 160 }}
        >
          <AppMenuItem value="all">All types</AppMenuItem>
          <AppMenuItem value="bug">Bug</AppMenuItem>
          <AppMenuItem value="feedback">Feedback</AppMenuItem>
          <AppMenuItem value="question">Question</AppMenuItem>
        </AppSelect>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : isError ? (
        <Alert severity="error">Could not load reports.</Alert>
      ) : items.length === 0 ? (
        <Alert severity="info">No reports yet.</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((item) => (
            <Paper key={item.id} variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                {typeIcon(item.type)}
                <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
                  {item.title}
                </Typography>
                <Chip size="small" label={item.type} />
                {item.severity && <Chip size="small" label={item.severity} color={item.severity === 'critical' || item.severity === 'high' ? 'error' : 'default'} />}
                <Chip size="small" label={item.status.replace('_', ' ')} color={statusColor(item.status)} />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {item.createdByName || item.createdBy} · {dayjs(item.createdAt).format('D MMM YYYY, h:mm A')}
                {item.pagePath ? ` · ${item.pagePath}` : ''}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                {item.description}
              </Typography>
              <TextField
                label="Admin notes"
                size="small"
                fullWidth
                multiline
                minRows={2}
                sx={{ mt: 1.5 }}
                value={notes[item.id] ?? item.adminNotes ?? ''}
                onChange={(e) => setNotes((prev) => ({ ...prev, [item.id]: e.target.value }))}
                onBlur={() => {
                  const value = notes[item.id];
                  if (value !== undefined && value !== (item.adminNotes ?? '')) {
                    updateMutation.mutate({ id: item.id, payload: { adminNotes: value } });
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" onClick={() => setStatus(item, 'in_progress')}>
                  In progress
                </Button>
                <Button size="small" variant="outlined" color="success" onClick={() => setStatus(item, 'resolved')}>
                  Resolve
                </Button>
                <Button size="small" variant="outlined" color="inherit" onClick={() => setStatus(item, 'closed')}>
                  Close
                </Button>
                {item.status !== 'open' && (
                  <Button size="small" onClick={() => setStatus(item, 'open')}>
                    Reopen
                  </Button>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AppIssuesTab;
